/**
 * Supplier Service
 * 
 * ISO 27001-compliant supplier management service with comprehensive
 * third-party risk assessment and due diligence capabilities.
 * 
 * Aligns with ISO 27001:2022 Clause 5.19 and Annex A controls (A.5.19–A.5.23)
 */

import { buildApiUrl, getDefaultHeaders } from './api';

// ============================================================================
// TYPE DEFINITIONS - ISO 27001 Compliant Supplier Management
// ============================================================================

/**
 * Service Category Classification
 * Based on ISO 27001 requirements for third-party service categorization
 */
export type ServiceCategory = 
  | 'cloud-hosting'
  | 'saas-application'
  | 'consultancy'
  | 'managed-services'
  | 'hr-services'
  | 'legal-services'
  | 'financial-services'
  | 'it-infrastructure'
  | 'software-development'
  | 'data-processing'
  | 'security-services'
  | 'other';

/**
 * Information Type Classification
 * Determines data protection requirements and risk level
 */
export type InformationType = 
  | 'personal-data'
  | 'financial-data'
  | 'confidential-business'
  | 'internal-only'
  | 'public'
  | 'special-category-data'
  | 'intellectual-property';

/**
 * Criticality Rating
 * Business dependency and impact assessment
 */
export type CriticalityRating = 'critical' | 'high' | 'medium' | 'low';

/**
 * Risk Rating
 * Overall supplier risk assessment score
 */
export type RiskRating = 'critical' | 'high' | 'medium' | 'low';

/**
 * Compliance Status
 * Current compliance and certification status
 */
export type ComplianceStatus = 'compliant' | 'partially-compliant' | 'non-compliant' | 'under-review' | 'not-assessed';

/**
 * Supplier Status
 * Lifecycle status of supplier relationship
 */
export type SupplierStatus = 'active' | 'inactive' | 'pending-approval' | 'under-review' | 'terminated';

/**
 * Review Frequency
 * How often supplier should be reassessed
 */
export type ReviewFrequency = 'monthly' | 'quarterly' | 'semi-annually' | 'annually';

/**
 * Supplier Tier Model
 * Classification based on criticality and exposure
 */
export type SupplierTier = 'tier-1-critical' | 'tier-2-support' | 'tier-3-low-risk';

// ============================================================================
// SECTION 1: SUPPLIER IDENTIFICATION
// ============================================================================

export interface SupplierIdentification {
  supplierName: string;
  tradingName?: string;
  registeredAddress: string;
  country: string;
  companyNumber?: string;
  registrationId?: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  businessOwnerInternal: string; // Internal relationship owner
  businessOwnerEmail: string;
}

// ============================================================================
// SECTION 2: SERVICE DESCRIPTION AND CLASSIFICATION
// ============================================================================

export interface ServiceClassification {
  serviceProvided: string; // Description of what supplier delivers
  serviceCategory: ServiceCategory;
  informationTypesAccessed: InformationType[]; // Can process multiple types
  criticalityRating: CriticalityRating;
  dataProcessingInvolved: boolean;
  dataProcessingLocation?: string; // Country/jurisdiction
  thirdPartySubProcessors: boolean;
  subProcessorsList?: string[]; // List of sub-processors if applicable
}

// ============================================================================
// SECTION 3: RISK AND SECURITY ASSESSMENT
// ============================================================================

export interface SecurityAssessment {
  initialRiskScore: RiskRating;
  riskOwnerInternal: string; // Person monitoring risk
  securityQuestionnaireCompleted: boolean;
  securityQuestionnaireDate?: string;
  infoSecPolicyAvailable: boolean;
  iso27001Certified: boolean;
  iso27001CertificateExpiry?: string;
  iso27001CertificateNumber?: string;
  penTestEvidence: boolean;
  lastPenTestDate?: string;
  penTestSummary?: string;
  dataProtectionCompliance: boolean;
  dataProtectionEvidence?: string; // e.g., ICO registration
  bcpDrPlanEvidence: boolean;
  bcpLastReviewDate?: string;
  cyberInsuranceCoverage: boolean;
  cyberInsuranceDetails?: string;
}

// ============================================================================
// SECTION 4: CONTRACTUAL AND LEGAL CONTROLS
// ============================================================================

export interface ContractualControls {
  contractInPlace: boolean;
  contractReference?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractRenewalDate?: string;
  terminationNoticePeriod?: string; // e.g., "30 days", "90 days"
  securityDataProtectionClauses: boolean;
  rightToAuditClause: boolean;
  confidentialityNdaSigned: boolean;
  ndaSignedDate?: string;
  dataProcessingAgreement: boolean;
  dpaSignedDate?: string;
  serviceLevelAgreement: boolean;
  slaAvailability?: string; // e.g., "99.9%"
  slaResponseTime?: string; // e.g., "4 hours"
  subcontractingRestrictions: boolean;
}

// ============================================================================
// SECTION 5: ONGOING MANAGEMENT AND MONITORING
// ============================================================================

export interface OngoingManagement {
  performanceReviewFrequency: ReviewFrequency;
  lastReviewDate?: string;
  nextReviewDate?: string;
  issuesOrIncidentsLogged: boolean;
  incidentCount?: number;
  recentSecurityIncidents?: string; // Description
  incidentRemediationStatus?: 'open' | 'in-progress' | 'closed';
  changeNotificationsReceived: boolean;
  lastChangeNotificationDate?: string;
  complianceCheckFrequency: ReviewFrequency;
  lastComplianceCheckDate?: string;
  auditResults?: string; // Link or summary
  auditFindings?: string;
  remediationStatus?: 'open' | 'in-progress' | 'closed';
}

// ============================================================================
// SECTION 6: TERMINATION AND EXIT
// ============================================================================

export interface TerminationExit {
  exitPlanInPlace: boolean;
  dataReturnDestructionProcess?: string;
  dataDestructionConfirmed: boolean;
  dataDestructionDate?: string;
  dataDestructionEvidence?: string;
  replacementSupplierIdentified: boolean;
  replacementSupplierName?: string;
  finalReviewCompleted: boolean;
  finalReviewDate?: string;
  finalReviewBy?: string;
}

// ============================================================================
// SECTION 7: OPTIONAL FIELDS
// ============================================================================

export interface OptionalFields {
  supplierTier?: SupplierTier;
  contractValue?: number; // Financial exposure
  contractCurrency?: string; // e.g., "GBP", "USD"
  jurisdictionRiskRating?: RiskRating;
  vendorEsgPosture?: string; // ESG/sustainability info
  notes?: string; // Free-text field
}

// ============================================================================
// MAIN SUPPLIER INTERFACE
// ============================================================================

/**
 * Comprehensive Supplier Interface
 * ISO 27001:2022 Clause 5.19 compliant
 * Annex A Controls: A.5.19, A.5.20, A.5.21, A.5.22, A.5.23
 */
export interface Supplier {
  id: string;
  status: SupplierStatus;
  complianceStatus: ComplianceStatus;
  
  // Core sections
  identification: SupplierIdentification;
  serviceClassification: ServiceClassification;
  securityAssessment: SecurityAssessment;
  contractualControls: ContractualControls;
  ongoingManagement: OngoingManagement;
  terminationExit: TerminationExit;
  optional: OptionalFields;
  
  // Process linkage (suppliers can be linked to one or more processes)
  linkedProcesses: string[]; // Array of process IDs
  
  // Audit trail
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface SupplierListResponse {
  suppliers: Supplier[];
  total: number;
  filtered?: number;
}

export interface SupplierFilters {
  status?: SupplierStatus[];
  criticalityRating?: CriticalityRating[];
  riskRating?: RiskRating[];
  complianceStatus?: ComplianceStatus[];
  serviceCategory?: ServiceCategory[];
  tier?: SupplierTier[];
  linkedProcess?: string; // Filter by specific process
  search?: string; // Search supplier name, trading name, service
}

// ============================================================================
// API OPERATIONS
// ============================================================================

/**
 * Get all suppliers with optional filtering
 */
export async function getAllSuppliers(filters?: SupplierFilters): Promise<ApiResponse<SupplierListResponse>> {
  try {
    let url = buildApiUrl('/suppliers');
    
    // Add query parameters for filters if provided
    if (filters) {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status.join(','));
      if (filters.criticalityRating) params.append('criticalityRating', filters.criticalityRating.join(','));
      if (filters.riskRating) params.append('riskRating', filters.riskRating.join(','));
      if (filters.complianceStatus) params.append('complianceStatus', filters.complianceStatus.join(','));
      if (filters.serviceCategory) params.append('serviceCategory', filters.serviceCategory.join(','));
      if (filters.tier) params.append('tier', filters.tier.join(','));
      if (filters.linkedProcess) params.append('linkedProcess', filters.linkedProcess);
      if (filters.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting suppliers:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch suppliers',
    };
  }
}

/**
 * Get supplier by ID
 */
export async function getSupplierById(id: string): Promise<ApiResponse<Supplier>> {
  try {
    const url = buildApiUrl(`/suppliers/${id}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting supplier:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch supplier',
    };
  }
}

/**
 * Create new supplier
 */
export async function createSupplier(supplier: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Supplier>> {
  try {
    const url = buildApiUrl('/suppliers');
    const response = await fetch(url, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify(supplier),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating supplier:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create supplier',
    };
  }
}

/**
 * Update existing supplier
 */
export async function updateSupplier(id: string, updates: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
  try {
    const url = buildApiUrl(`/suppliers/${id}`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update supplier',
    };
  }
}

/**
 * Delete supplier
 */
export async function deleteSupplier(id: string): Promise<ApiResponse<void>> {
  try {
    const url = buildApiUrl(`/suppliers/${id}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete supplier',
    };
  }
}

/**
 * Link supplier to process
 */
export async function linkSupplierToProcess(supplierId: string, processId: string): Promise<ApiResponse<Supplier>> {
  try {
    const url = buildApiUrl(`/suppliers/${supplierId}/processes/${processId}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error linking supplier to process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to link supplier to process',
    };
  }
}

/**
 * Unlink supplier from process
 */
export async function unlinkSupplierFromProcess(supplierId: string, processId: string): Promise<ApiResponse<Supplier>> {
  try {
    const url = buildApiUrl(`/suppliers/${supplierId}/processes/${processId}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error unlinking supplier from process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to unlink supplier from process',
    };
  }
}

/**
 * Get suppliers by process ID
 */
export async function getSuppliersByProcess(processId: string): Promise<ApiResponse<Supplier[]>> {
  try {
    const url = buildApiUrl(`/processes/${processId}/suppliers`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting suppliers by process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch suppliers for process',
    };
  }
}
